"use client";

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  ReactNode,
} from "react";
import { useRouter } from "next/navigation";
import { supabase } from "@/lib/supabase";
import { User as AuthUser } from "@supabase/supabase-js";
import { useToast } from "./ToastContext";
import { useSupabase } from "@/components/SupabaseProvider";
import { getCoordinatesForLocation } from "@/lib/locationData";
import LocationContext from "@/contexts/LocationContext";
import { User, Coordinates } from "@/lib/types"; // Import from centralized types
import { mapSupabaseProfileToUser } from "@/lib/typeMappers";
import logger from "@/lib/utils/logger";
import posthog from "posthog-js";
import { identifyPostHogUser } from "@/lib/analytics/posthogIdentify";
import {
  navigateToHome,
  sanitizeInternalPath,
} from "@/lib/navigation/authRedirect";
import {
  mergeResolvedLocationIds,
  normalizeResolvedLocationIds,
  resolveLocationIdsFromLocationString,
  type ResolvedLocationIds,
} from "@/lib/locationIds";
import {
  ensureGrowthPhaseNotificationPreferences,
  nearMeEnabledForProfileLocation,
} from "@/lib/notifications/ensure-notification-preferences";
import { hasRequiredProfileFields } from "@/lib/profileCompletion";
import type { Database, Json } from "@/lib/database.types";
import {
  disableNearMeWhatsappIfPhoneInvalid,
  syncSellerAvailabilityWhatsappForPhoneChange,
} from "@/lib/notifications/near-me-whatsapp-preference-sync";

type ProfileUpdate = Database["public"]["Tables"]["profiles"]["Update"];

// Re-export User type for backward compatibility with existing imports
export type { User };

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  hasAuthResolved: boolean;
  isAdmin: boolean;
  login: (
    email: string,
    password: string,
    captchaToken?: string,
  ) => Promise<{ success: boolean; message: string; user?: User | null }>;
  signup: (
    name: string,
    email: string,
    password: string,
    captchaToken?: string,
    phone?: string,
    location?: string,
    gender?: string,
    coordinates?: { lat: number; lng: number },
    placeId?: string,
    locationIds?: ResolvedLocationIds,
    discoveryChannel?: string,
    referralCode?: string,
  ) => Promise<{
    success: boolean;
    message: string;
    needsVerification?: boolean;
  }>;
  loginWithGoogle: (
    redirectToInternal?: string | null,
    referralCode?: string | null,
  ) => Promise<{ success: boolean; message: string }>;
  logout: () => void;
  requireAuth: (redirectTo?: string) => boolean;
  updateUser: (userData: Partial<User>) => Promise<void>;
  refreshUser: () => Promise<void>;
  verifyEmail: (
    otpCode: string,
    email: string,
  ) => Promise<{ success: boolean; message: string; user?: User | null }>;
  resendVerificationEmail: (
    email: string,
  ) => Promise<{ success: boolean; message: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasAuthResolved, setHasAuthResolved] = useState(false);
  const router = useRouter();
  const { showToast } = useToast();
  const {
    session,
    isLoading: isSupabaseLoading,
    error: supabaseError,
  } = useSupabase();
  const locationContext = useContext(LocationContext);

  // Map Supabase user data to our User interface
  const mapUser = async (authUser: AuthUser): Promise<User> => {
    try {
      // Get user profile from profiles table
      const { data: profile, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", authUser.id)
        .maybeSingle();

      if (error) {
        console.error("Error fetching profile:", error);
        throw error;
      }

      // If profile doesn't exist, create a minimal profile
      if (!profile) {
        // Create a profile with available user metadata
        const userMetadata = authUser.user_metadata || {};

        // Attempt to create a profile
        try {
          const timestamp = new Date().toISOString();
          const newProfile = {
            id: authUser.id,
            name: userMetadata.name || "User",
            email: authUser.email || "",
            phone: userMetadata.phone || "",
            location: userMetadata.location || "",
            location_state_id: userMetadata.location_state_id || null,
            location_lga_id: userMetadata.location_lga_id || null,
            location_neighborhood_id:
              userMetadata.location_neighborhood_id || null,
            gender: userMetadata.gender || "",
            coordinates: userMetadata.coordinates || null,
            place_id: userMetadata.place_id || null,
            created_at: timestamp,
            updated_at: timestamp,
            is_admin: false,
            is_profile_complete: false,
          };

          const { error: profileError } = await supabase
            .from("profiles")
            .upsert(newProfile);

          if (profileError) {
            console.error("Error creating missing profile:", profileError);
          } else {
            await ensureGrowthPhaseNotificationPreferences(
              supabase,
              authUser.id,
              {
                nearMeEnabled: nearMeEnabledForProfileLocation({
                  location: userMetadata.location,
                  location_state_id: userMetadata.location_state_id,
                  location_lga_id: userMetadata.location_lga_id,
                  location_neighborhood_id:
                    userMetadata.location_neighborhood_id,
                }),
                phoneDigits: userMetadata.phone,
              },
            );
          }
        } catch (profileError) {
          console.error("Exception creating missing profile:", profileError);
        }

        return {
          id: authUser.id,
          name: userMetadata.name || "User",
          email: authUser.email || "",
          isAdmin: false,
          isEmailVerified: authUser.email_confirmed_at != null, // Check if email is verified
          phone: userMetadata.phone || "",
          location: userMetadata.location || "",
          avatar_url: userMetadata.avatar_url || null,
          gender: userMetadata.gender || "",
          coordinates: userMetadata.coordinates || null,
          is_profile_complete: false,
          createdAt: new Date(authUser.created_at || Date.now()),
          updatedAt: new Date(authUser.updated_at || Date.now()),
        };
      }

      void ensureGrowthPhaseNotificationPreferences(supabase, authUser.id, {
        nearMeEnabled: nearMeEnabledForProfileLocation({
          location: profile.location,
          location_state_id: profile.location_state_id,
          location_lga_id: profile.location_lga_id,
          location_neighborhood_id: profile.location_neighborhood_id,
        }),
        phoneDigits: profile.phone ?? authUser.user_metadata?.phone,
      });

      // Map the profile to our User type using the type mapper
      const mappedUser = mapSupabaseProfileToUser(profile);
      return {
        ...mappedUser,
        isEmailVerified: authUser.email_confirmed_at != null, // Check if email is verified
        // Override with auth user metadata if profile is missing fields
        name: mappedUser.name || authUser.user_metadata?.name || "User",
        email: mappedUser.email || authUser.email || "",
        phone: mappedUser.phone || authUser.user_metadata?.phone || undefined,
        location:
          mappedUser.location || authUser.user_metadata?.location || undefined,
        location_state_id:
          mappedUser.location_state_id ??
          authUser.user_metadata?.location_state_id ??
          null,
        location_lga_id:
          mappedUser.location_lga_id ??
          authUser.user_metadata?.location_lga_id ??
          null,
        location_neighborhood_id:
          mappedUser.location_neighborhood_id ??
          authUser.user_metadata?.location_neighborhood_id ??
          null,
        avatar_url:
          mappedUser.avatar_url ||
          authUser.user_metadata?.avatar_url ||
          undefined,
        gender:
          mappedUser.gender || authUser.user_metadata?.gender || undefined,
        coordinates:
          mappedUser.coordinates ||
          (authUser.user_metadata?.coordinates as Coordinates | null) ||
          null,
      };
    } catch (error) {
      console.error("Error mapping user:", error);

      // Return a minimal user object when profile mapping fails
      const userMetadata = authUser.user_metadata || {};

      return {
        id: authUser.id,
        name: userMetadata.name || "User",
        email: authUser.email || "",
        isAdmin: false,
        isEmailVerified: authUser.email_confirmed_at != null, // Check if email is verified
        createdAt: new Date(authUser.created_at || Date.now()),
        updatedAt: new Date(authUser.updated_at || Date.now()),
        phone: userMetadata.phone || "",
        location: userMetadata.location || "",
        gender: userMetadata.gender || "",
        coordinates: userMetadata.coordinates || null,
        is_profile_complete: false,
      };
    }
  };

  // Track if we've already updated location to prevent infinite loops
  const locationUpdateRef = useRef<string | null>(null);

  // Separate effect for handling initial auth state
  useEffect(() => {
    if (supabaseError) {
      console.error("Supabase auth error:", supabaseError);
      showToast("Authentication error. Please try logging in again.", "error");
      const timer = setTimeout(() => {
        setUser(null);
        setIsAuthenticated(false);
        setIsLoading(false);
        setHasAuthResolved(true);
      }, 0);
      return () => clearTimeout(timer);
    }

    // If we have a session, map the user
    const handleSession = async () => {
      if (session) {
        try {
          const mappedUser = await mapUser(session.user);
          setUser(mappedUser);
          setIsAuthenticated(true);

          void identifyPostHogUser(mappedUser.id, {
            email: mappedUser.email,
            name: mappedUser.name,
            location: mappedUser.location,
            createdAt: mappedUser.createdAt,
            isEmailVerified: mappedUser.isEmailVerified,
            fetchListingCount: true,
          });

          // Update location from profile on session restore
          // Only update if location is different to prevent duplicate geocoding
          if (
            locationContext &&
            mappedUser.location &&
            locationContext.userLocation?.areaName !== mappedUser.location &&
            locationUpdateRef.current !== mappedUser.location
          ) {
            logger.log(
              "[AUTH] Setting location from profile on session restore:",
              mappedUser.location,
            );
            locationUpdateRef.current = mappedUser.location;
            locationContext.updateLocationFromProfile(mappedUser.location);
          }
        } catch (error) {
          console.error("Error mapping user from session:", error);
          setUser(null);
          setIsAuthenticated(false);
        }
      } else {
        setUser(null);
        setIsAuthenticated(false);
        locationUpdateRef.current = null; // Reset when logged out
      }

      setIsLoading(false);
      setHasAuthResolved(true);
    };

    if (!isSupabaseLoading) {
      handleSession();
    }
  }, [session, isSupabaseLoading, supabaseError, showToast, locationContext]);

  const login = async (
    email: string,
    password: string,
    captchaToken?: string,
  ): Promise<{ success: boolean; message: string; user?: User | null }> => {
    setIsLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: {
          captchaToken,
        },
      });

      if (error) {
        showToast(error.message || "Failed to login", "error");
        return { success: false, message: error.message || "Failed to login" };
      }

      if (!data.user) {
        showToast("No user data returned", "error");
        return { success: false, message: "No user data returned" };
      }

      // Check if email is verified
      if (data.user.email_confirmed_at === null) {
        // Create a new verification email
        await supabase.auth.resend({
          type: "signup",
          email: email,
        });

        await supabase.auth.signOut();

        return {
          success: false,
          message:
            "Please verify your email before logging in. A new verification email has been sent.",
        };
      }

      // Map the user data
      const mappedUser = await mapUser(data.user);
      setUser(mappedUser);
      setIsAuthenticated(true);

      void identifyPostHogUser(mappedUser.id, {
        email: mappedUser.email,
        name: mappedUser.name,
        location: mappedUser.location,
        createdAt: mappedUser.createdAt,
        isEmailVerified: mappedUser.isEmailVerified,
        fetchListingCount: true,
      });
      posthog.capture("user_logged_in", { method: "email" });

      // Update location after successful login
      if (locationContext && mappedUser.location) {
        logger.log("[AUTH] Setting location after login:", mappedUser.location);
        locationContext.updateLocationFromProfile(mappedUser.location);
      }

      return {
        success: true,
        message: "Welcome back, " + mappedUser.name + "!",
        user: mappedUser,
      };
    } catch (error) {
      console.error("Login error:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "An error occurred during login";
      return { success: false, message: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const signup = async (
    name: string,
    email: string,
    password: string,
    captchaToken?: string,
    phone?: string,
    location?: string,
    gender?: string,
    coordinates?: { lat: number; lng: number },
    placeId?: string,
    locationIds?: ResolvedLocationIds,
    discoveryChannel?: string,
    referralCode?: string,
  ): Promise<{
    success: boolean;
    message: string;
    needsVerification?: boolean;
  }> => {
    setIsLoading(true);

    try {
      // Check if user exists first to avoid 500 error from supabase
      const { data: existingUsers, error: existingUsersError } = await supabase
        .from("profiles")
        .select("id")
        .eq("email", email);

      if (existingUsersError) {
        console.error("Error checking existing users:", existingUsersError);
      }

      if (existingUsers && existingUsers.length > 0) {
        showToast("Email already in use", "error");
        return { success: false, message: "Email already in use" };
      }

      // Use provided coordinates or extract from location string if available
      let finalCoordinates = coordinates;
      if (!finalCoordinates && location) {
        finalCoordinates = getCoordinatesForLocation(location);
        logger.log(
          "[AUTH] Extracted coordinates for signup location:",
          location,
          finalCoordinates,
        );
      }

      const resolvedLocationIds = mergeResolvedLocationIds(
        locationIds,
        location,
      );

      // Prepare user metadata with all profile fields including Google Maps data
      const userMetadata = {
        name,
        phone,
        location,
        location_state_id: resolvedLocationIds.locationStateId,
        location_lga_id: resolvedLocationIds.locationLgaId,
        location_neighborhood_id: resolvedLocationIds.locationNeighborhoodId,
        gender,
        coordinates: finalCoordinates,
        place_id: placeId,
        // Store the initial location to ensure it's available after verification
        initial_location: location,
      };

      logger.log("[AUTH] Signup user metadata:", userMetadata);

      // Register user with Supabase Auth
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userMetadata,
          emailRedirectTo: `${window.location.origin}/verify-account`,
          captchaToken,
        },
      });

      if (error && Object.keys(error).length > 0) {
        console.error("Signup error from Supabase:", error);
        showToast(error.message || "Failed to create account", "error");
        throw error;
      } else if (error && Object.keys(error).length === 0) {
        console.warn(
          "Empty error object received during auth signup, continuing anyway",
        );
      }

      if (!data.user) {
        console.error("No user data returned from signUp call");
        showToast("Failed to create account - no user data returned", "error");
        return {
          success: false,
          message: "Failed to create account - no user data returned",
        };
      }

      const userId = data.user.id;

      // Create timestamp for audit fields
      const timestamp = new Date().toISOString();

      // Create a complete profile record with all fields including Google Maps data
      const profileData = {
        id: userId,
        email: email,
        name: name,
        phone: phone || null,
        location: location || null,
        location_state_id: resolvedLocationIds.locationStateId,
        location_lga_id: resolvedLocationIds.locationLgaId,
        location_neighborhood_id: resolvedLocationIds.locationNeighborhoodId,
        gender: gender || null,
        coordinates: finalCoordinates || null,
        place_id: placeId || null,
        discovery_channel: discoveryChannel || null,
        referral_code: referralCode || null,
        created_at: timestamp,
        updated_at: timestamp,
        is_admin: false,
        is_profile_complete: false,
      };

      logger.log("[AUTH] Creating profile with data:", profileData);

      // IMPORTANT: Use a function call to create the profile since it bypasses RLS
      try {
        // Call a server function (or API endpoint) to create the profile
        // This requires setting up a server-side API route or Edge Function
        const response = await fetch("/api/create-profile", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(profileData),
        });

        if (!response.ok) {
          const responseData = await response.json();
          console.error("Error creating profile from API:", responseData);
          // Continue with signup process despite error
        } else {
          logger.log("Profile created successfully via API");
        }
      } catch (profileCreationError) {
        console.error(
          "Exception during profile creation via API:",
          profileCreationError,
        );
        // Continue with signup process despite error
      }

      void identifyPostHogUser(userId, {
        email,
        name,
        location: location ?? undefined,
        createdAt: new Date(),
        isEmailVerified: false,
        listingCount: 0,
      });
      posthog.capture("user_signed_up", {
        method: "email",
        has_phone: Boolean(phone),
        has_location: Boolean(location),
        has_referral_code: Boolean(referralCode),
        discovery_channel: discoveryChannel ?? null,
      });

      // Sign out the user until they verify their email
      await supabase.auth.signOut();
      posthog.reset();

      return {
        success: true,
        message:
          "Account created successfully! Please check your email to verify your account.",
        needsVerification: true,
      };
    } catch (error) {
      console.error("Error signing up:", error);

      // Check if the error is an empty object or null/undefined
      if (
        !error ||
        (typeof error === "object" && Object.keys(error).length === 0) ||
        error === null ||
        error === undefined
      ) {
        // Treat empty errors as success (Supabase sometimes returns empty errors)
        return {
          success: true,
          message:
            "Account created successfully! Please check your email to verify your account.",
          needsVerification: true,
        };
      }

      const errorMessage =
        error instanceof Error ? error.message : "Failed to create account";
      return {
        success: false,
        message: errorMessage,
      };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      setIsLoading(true);

      // Reset location to default before signing out
      if (locationContext) {
        logger.log("[AUTH] Resetting location before logout");
        locationContext.updateLocationFromProfile(undefined);
      }

      await supabase.auth.signOut();
      setUser(null);
      setIsAuthenticated(false);

      posthog.reset();
      showToast("Logged out successfully", "success");
      navigateToHome(router);
    } catch (error) {
      console.error("Error logging out:", error);
      showToast("Error logging out", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const requireAuth = (redirectTo: string = "/login") => {
    if (isLoading) return false; // Still checking auth state

    if (!isAuthenticated) {
      showToast("Please log in to continue", "info");
      router.push(redirectTo);
      return false;
    }

    return true;
  };

  const updateUser = async (userData: Partial<User>) => {
    if (!user) return;

    try {
      const processedUserData = { ...userData };
      let newCoordinates: { lat: number; lng: number } | null =
        user.coordinates || null; // Keep existing coordinates by default

      // Only look up coordinates if none were provided (for backwards compatibility)
      // This allows Google Maps coordinates to be preserved when explicitly provided
      if (userData.location && userData.location !== user.location) {
        // If coordinates are explicitly provided (e.g., from Google Maps), use them
        if (userData.coordinates) {
          logger.log(
            "[AUTH] Using provided coordinates for location update:",
            userData.coordinates,
          );
          newCoordinates = userData.coordinates;
        } else {
          // Otherwise fall back to local coordinate lookup
          logger.log(
            "[AUTH] No coordinates provided, looking up local coordinates for:",
            userData.location,
          );
          newCoordinates = getCoordinatesForLocation(userData.location) || null;
        }
        processedUserData.coordinates = newCoordinates;
      } else if (userData.location === null || userData.location === "") {
        // Handle case where location is cleared
        newCoordinates = null;
        processedUserData.coordinates = null;
        // Also clear place_id when location is cleared
        processedUserData.place_id = undefined;
      }

      // Log what we're about to save for debugging
      console.log("[AUTH] Updating profile with processed data:", {
        location: processedUserData.location,
        coordinates: processedUserData.coordinates,
        place_id: processedUserData.place_id,
      });

      // Convert User type fields to database field names for Supabase
      const dbUpdateData: ProfileUpdate = {
        updated_at: new Date().toISOString(),
      };

      // Map User type fields to database field names
      if (processedUserData.name !== undefined)
        dbUpdateData.name = processedUserData.name;
      if (processedUserData.phone !== undefined)
        dbUpdateData.phone = processedUserData.phone;
      if (processedUserData.location !== undefined)
        dbUpdateData.location = processedUserData.location;
      if (processedUserData.location_state_id !== undefined) {
        dbUpdateData.location_state_id = processedUserData.location_state_id;
      }
      if (processedUserData.location_lga_id !== undefined) {
        dbUpdateData.location_lga_id = processedUserData.location_lga_id;
      }
      if (processedUserData.location_neighborhood_id !== undefined) {
        dbUpdateData.location_neighborhood_id =
          processedUserData.location_neighborhood_id;
      }
      if (processedUserData.gender !== undefined)
        dbUpdateData.gender = processedUserData.gender;
      if (processedUserData.coordinates !== undefined)
        dbUpdateData.coordinates =
          processedUserData.coordinates as unknown as Json;
      if (processedUserData.place_id !== undefined)
        dbUpdateData.place_id = processedUserData.place_id;
      if (processedUserData.avatar_url !== undefined)
        dbUpdateData.avatar_url = processedUserData.avatar_url;
      if (processedUserData.is_profile_complete !== undefined)
        dbUpdateData.is_profile_complete =
          processedUserData.is_profile_complete;
      if (processedUserData.discovery_channel !== undefined)
        dbUpdateData.discovery_channel = processedUserData.discovery_channel;

      const { error } = await supabase
        .from("profiles")
        .update(dbUpdateData)
        .eq("id", user.id);

      if (error) throw error;

      // Update user in state with all processed data
      setUser((prev) => (prev ? { ...prev, ...processedUserData } : null));

      if (processedUserData.phone !== undefined) {
        const previousPhone = user.phone ?? null;
        const nextPhone =
          processedUserData.phone === null || processedUserData.phone === ""
            ? null
            : String(processedUserData.phone);
        void disableNearMeWhatsappIfPhoneInvalid(supabase, user.id, nextPhone);
        void syncSellerAvailabilityWhatsappForPhoneChange(
          supabase,
          user.id,
          previousPhone,
          nextPhone,
        );
      }

      // If location was updated (string), call LocationContext's update function
      // This allows LocationContext to maintain its own representation if needed
      if (
        userData.location !== undefined &&
        locationContext?.updateLocationFromProfile
      ) {
        locationContext.updateLocationFromProfile(userData.location);
      }

      // showToast('Profile updated successfully', 'success');
    } catch (error) {
      console.error("Error updating user:", error);
      showToast("Failed to update profile", "error");
    }
  };

  const refreshUser = async (): Promise<void> => {
    try {
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError) {
        console.error("Error getting session:", sessionError);
        setUser(null);
        setIsAuthenticated(false);
        return;
      }

      if (session?.user) {
        const mappedUser = await mapUser(session.user);
        setUser(mappedUser);
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error("Error refreshing user:", error);
      // Don't throw - allow calling code to handle gracefully
    }
  };

  // Add a function to verify email with OTP code
  const verifyEmail = async (
    otpCode: string,
    email: string,
  ): Promise<{ success: boolean; message: string; user?: User | null }> => {
    try {
      setIsLoading(true);

      // Verify the user's email with the OTP code
      const { error } = await supabase.auth.verifyOtp({
        type: "signup",
        token: otpCode,
        email: email,
      });

      if (error) {
        console.error("Error verifying email:", error);
        return {
          success: false,
          message: error.message || "Failed to verify email",
        };
      }

      // Get the current session to check if the user is now verified
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (session?.user) {
        let verifiedUser: User | null = null;
        // Update the existing profile to mark it as verified
        try {
          const userId = session.user.id;
          const timestamp = new Date().toISOString();

          // First check if the profile already exists
          const { data: existingProfile, error: fetchError } = await supabase
            .from("profiles")
            .select("*")
            .eq("id", userId)
            .single();

          if (fetchError) {
            console.error(
              "Error fetching existing profile during verification:",
              fetchError,
            );
          }

          // Update the profile with verified status and any missing metadata
          const userMetadata = session.user.user_metadata || {};
          const fallbackLocation =
            existingProfile?.location || userMetadata.location || null;
          const resolvedLocationIds =
            resolveLocationIdsFromLocationString(fallbackLocation);
          const normalizedProfileLocationIds = normalizeResolvedLocationIds(
            {
              locationStateId:
                existingProfile?.location_state_id ||
                userMetadata.location_state_id ||
                resolvedLocationIds.locationStateId,
              locationLgaId:
                existingProfile?.location_lga_id ||
                userMetadata.location_lga_id ||
                resolvedLocationIds.locationLgaId,
              locationNeighborhoodId:
                existingProfile?.location_neighborhood_id ||
                userMetadata.location_neighborhood_id ||
                resolvedLocationIds.locationNeighborhoodId,
            },
            fallbackLocation,
          );
          const profileLocationStateId =
            normalizedProfileLocationIds.locationStateId;
          const profileLocationLgaId =
            normalizedProfileLocationIds.locationLgaId;
          const profileLocationNeighborhoodId =
            normalizedProfileLocationIds.locationNeighborhoodId;
          const profileName =
            existingProfile?.name || userMetadata.name || null;
          const profilePhone =
            existingProfile?.phone || userMetadata.phone || null;
          const profileGender =
            existingProfile?.gender || userMetadata.gender || null;
          const profileLocation = fallbackLocation;
          const isProfileComplete = hasRequiredProfileFields({
            phone: profilePhone ?? undefined,
            gender: profileGender ?? undefined,
            name: profileName ?? "",
            location: profileLocation ?? undefined,
            location_state_id: profileLocationStateId,
            location_lga_id: profileLocationLgaId,
            location_neighborhood_id: profileLocationNeighborhoodId,
          });

          // Start with existing profile data (if available) to preserve all fields
          const profileData = {
            ...(existingProfile || {}),
            id: userId,
            email: session.user.email,
            name: profileName,
            phone: profilePhone,
            location: profileLocation,
            location_state_id: profileLocationStateId,
            location_lga_id: profileLocationLgaId,
            location_neighborhood_id: profileLocationNeighborhoodId,
            gender: profileGender,
            coordinates:
              existingProfile?.coordinates || userMetadata.coordinates,
            place_id: existingProfile?.place_id || userMetadata.place_id,
            updated_at: timestamp,
            is_admin: false,
            is_profile_complete: isProfileComplete,
          };

          // Update the profile
          const { error: profileError } = await supabase
            .from("profiles")
            .upsert(profileData);

          if (profileError) {
            console.error(
              "Error updating profile after verification:",
              profileError,
            );
          } else {
            logger.log("Profile updated and marked as verified:", userId);
            await ensureGrowthPhaseNotificationPreferences(supabase, userId, {
              nearMeEnabled: nearMeEnabledForProfileLocation({
                location: profileData.location,
                location_state_id: profileData.location_state_id,
                location_lga_id: profileData.location_lga_id,
                location_neighborhood_id: profileData.location_neighborhood_id,
              }),
              phoneDigits: profileData.phone,
            });
          }

          // Map the user data and update state
          verifiedUser = await mapUser(session.user);
          setUser(verifiedUser);
          setIsAuthenticated(true);

          void identifyPostHogUser(verifiedUser.id, {
            email: verifiedUser.email,
            name: verifiedUser.name,
            location: verifiedUser.location,
            createdAt: verifiedUser.createdAt,
            isEmailVerified: true,
            fetchListingCount: true,
          });
          posthog.capture("email_verified");

          // Update location after successful verification if location exists
          if (locationContext && profileData.location) {
            logger.log(
              "[AUTH] Setting location after verification:",
              profileData.location,
            );
            locationContext.updateLocationFromProfile(profileData.location);
          }
        } catch (profileUpdateError) {
          console.error(
            "Exception updating profile after verification:",
            profileUpdateError,
          );
        }

        if (!verifiedUser) {
          try {
            verifiedUser = await mapUser(session.user);
            setUser(verifiedUser);
            setIsAuthenticated(true);
          } catch (e) {
            console.error("Exception mapping user after verification:", e);
          }
        }

        return {
          success: true,
          message: "Email verified successfully! You are now logged in.",
          user: verifiedUser,
        };
      } else {
        // User was verified but not logged in
        return {
          success: true,
          message: "Email verified successfully! Please log in to continue.",
          user: null,
        };
      }
    } catch (error) {
      console.error("Exception verifying email:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to verify email";
      return { success: false, message: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const resendVerificationEmail = async (
    email: string,
  ): Promise<{ success: boolean; message: string }> => {
    try {
      setIsLoading(true);

      // Use custom API endpoint to resend verification email without captcha
      const response = await fetch("/api/auth/resend-verification", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const result = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: result.message || "Failed to resend verification email",
        };
      }

      return { success: true, message: "Verification email sent successfully" };
    } catch (error) {
      console.error("Exception resending verification email:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to resend verification email";
      return { success: false, message: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithGoogle = async (
    redirectToInternal?: string | null,
    referralCode?: string | null,
  ): Promise<{
    success: boolean;
    message: string;
  }> => {
    setIsLoading(true);

    try {
      const safeRedirect = sanitizeInternalPath(redirectToInternal);
      const safeReferralCode =
        typeof referralCode === "string" ? referralCode.trim() : "";
      const callbackBase = `${window.location.origin}/callback`;
      const callbackParams = new URLSearchParams();
      if (safeRedirect !== "/") {
        callbackParams.set("redirectTo", safeRedirect);
      }
      if (safeReferralCode) {
        callbackParams.set("ref", safeReferralCode);
      }
      const callbackUrl = callbackParams.toString()
        ? `${callbackBase}?${callbackParams.toString()}`
        : callbackBase;

      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: callbackUrl,
          queryParams: {
            access_type: "offline",
            prompt: "consent",
          },
        },
      });

      if (error) {
        showToast(error.message || "Failed to login with Google", "error");
        return {
          success: false,
          message: error.message || "Failed to login with Google",
        };
      }

      // Note: The actual user data will be handled by the session listener
      // since OAuth redirects to a new page
      return { success: true, message: "Redirecting to Google..." };
    } catch (error) {
      console.error("Google login error:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "An error occurred during Google login";
      return { success: false, message: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        hasAuthResolved,
        isAdmin: user?.isAdmin || false,
        login,
        signup,
        loginWithGoogle,
        logout,
        requireAuth,
        updateUser,
        refreshUser,
        verifyEmail,
        resendVerificationEmail,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
